import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Grid, GridCell, Modal, NotificationInline,
   NotificationManagerContext, Spacer, Text, View } from 'oio-react'
import { Route, useHistory, useRouteMatch } from 'react-router-dom'
import { DeleteIcon } from 'assets/icons'
import uiConstants from 'config/constants/ui'
import { settingsContentPagesUrl } from 'config/constants/urls'
import UrlActionListItemRow from 'src/components/UrlActionListItemRow'
import { Input } from 'src/components/ConnectedForm'
import ActionListItemRow from 'src/components/Settings/ActionListItemRow'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import { useUpdatePage } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import PageDeleteConfirm from '~/Page/components/DeleteConfirm'
import PageContext from '~/Page/libraries/context'
import UrlEdit from '~/Page/pages/Settings/General/UrlEdit'

const PageSettingsGeneral = () => {
   const history = useHistory()
   const match = useRouteMatch()

   const page = useContext(PageContext)
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updatePage } = useUpdatePage()

   const { showNotification } = useContext(NotificationManagerContext)
   const [error, setError] = useState(null)
   const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
   const [urlModalIsOpen, setUrlModalIsOpen] = useState(true)

   const handleSavePage = async (values, actions) => {
      setError(null)

      try {
         await updatePage({ id: page.id, withContent: false }, values)
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         setError(err)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <View
         display="flex"
         justifyContent="center"
         padding="24px[a] 30px[b] 30px[c] 48px[d-f]">
         <Formik
            initialValues={{
               slug: page.slug,
               title: page.title
            }}
            onSubmit={handleSavePage}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f3f3f3"
                  onSubmit={handleSubmit}>
                  <View maxWidth="100%[a-c] 570px[d-f]">
                     <View width="100%" display="flex">
                        <View flex="1 1 auto" display="none[a-b] block[c-f]" paddingRight="60px">
                           <ProtoHeadingBlock
                              title="Page Settings"
                              description="General Settings for this Page"
                           />
                        </View>
                        <View flex="0 0 auto" paddingTop="18px">
                           <Button
                              type="submit"
                              name="Save Changes"
                              color="#f7b77c"
                              mode={isSubmitting ? 'loading' : 'normal'}
                              textColor="#000"
                              size="sm"
                              padding="24px"
                           />
                        </View>
                     </View>
                     <Spacer size="6" />
                     {error && (
                        <NotificationInline
                           borderRadius="3px"
                           textSize="2"
                           type="error"
                           title="Error"
                           message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                        />
                     )}
                     <View
                        width="100%"
                        padding="18px"
                        borderRadius="3px"
                        backgroundColor="#fff">
                        <Grid>
                           <GridCell colspan="12[a-b] 4[c-f]">
                              <Text size="2" weight="medium">
                                 Page Title
                              </Text>
                              <Text size="2" color="#888">
                                 (max 20 characters)
                              </Text>
                           </GridCell>
                           <GridCell colspan="12[a-b] 8[c-f]">
                              <Input
                                 maxLength="20"
                                 size="md"
                                 name="title"
                                 placeholder="Page Title"
                              />
                           </GridCell>
                        </Grid>
                     </View>
                     <Spacer size="8" />
                     {!initiative && (
                        <UrlActionListItemRow
                           editUrl={`${match.url}/urls`}
                           entityName="Page"
                           globalUrl={page.globalUrls[0]}
                           slugUrl={`/pages/${page.slug}`}
                        />
                     )}
                     <ActionListItemRow
                        name="Delete this page"
                        description="Permanently delete this page. This action cannot be undone."
                        action={(
                           <Button
                              onClick={() => setDeleteModalIsOpen(true)}
                              width="78px"
                              size="xs"
                              name="Delete"
                              color="#eee"
                              textColor="#df4e34"
                           />
                        )}
                        icon={(
                           <DeleteIcon
                              width="18px"
                              height="18px"
                              strokeWidth="2px"
                              color="#aaa"
                           />
                        )}
                     />
                  </View>
               </Form>
            )}
         />
         <Modal
            borderRadius="6px"
            width="90%[a-b] 300px[c-f]"
            onCloseTrigger={() => setDeleteModalIsOpen(false)}
            open={deleteModalIsOpen}
            // TODO: This needs to be handled much better
            // It is set to +2 here because it might be 2 stacked 2 layers above
            // See Issue: #294
            zIndex={uiConstants.zIndexes.tmpSettingsModal + 2}>
            <PageDeleteConfirm
               pageId={page.id}
               pageTitle={page.title}
               onCancelButtonClick={() => setDeleteModalIsOpen(false)}
               onDelete={() => {
                  history.push(initiative
                     ? `/initiatives/${initiative.slug}/settings/content`
                     : settingsContentPagesUrl)
               }}
            />
         </Modal>
         <Route
            path={`${match.path}/urls`}
            render={() => (
               <Modal
                  borderRadius="6px[c-f]"
                  width="100%[a-b] 360px[c-f]"
                  height="100%[a-b] 450px[c-f]"
                  onCloseComplete={() => {
                     history.push(match.url)
                     setUrlModalIsOpen(true)
                  }}
                  onCloseTrigger={() => setUrlModalIsOpen(false)}
                  open={urlModalIsOpen}
                  // TODO: This needs to be handled much better
                  // It is set tp +2 here because it might be 2 stacked 2 layers above
                  // See Issue: #294
                  zIndex={uiConstants.zIndexes.tmpSettingsModal + 2}>
                  <UrlEdit
                     targetType="initiative"
                     onCloseButtonClick={() => setUrlModalIsOpen(false)}
                  />
               </Modal>
            )}
         />
      </View>
   )
}

export default PageSettingsGeneral
